/**
 * CostPerO2Page — Bryant 2011 / Gantzer 2009 lifetime-cost optimisation
 *
 * Ranks aeration scenarios by (£ lifetime cost) / (mg O₂ delivered over horizon).
 * Often redistributing existing modules outperforms adding new ones.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { PoundSterling, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, KEYS, onPersistChange } from '@/lib/sessionPersistence';
import {
  specFromLegacy, type LegacyModSpec,
  moduleAOTRkgh, blowerKwForModule,
} from '@/lib/diffuserPhysics';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

interface ModSpec extends LegacyModSpec { waterDepthM: number; mountingHeightM: number; waterTempC: number; currentDoMgL?: number; alphaFactor?: number; betaFactor?: number; }
const MOD_DEFAULTS: ModSpec = { diffuserCount: 8, diffuserType: 'membrane_disc_epdm', diffuserDiamMm: 300, poreSizeUm: 80, airflowPerDiffNm3h: 1.2, waterDepthM: 3.0, mountingHeightM: 0.40, waterTempC: 12, currentDoMgL: 2.0, alphaFactor: 0.85, betaFactor: 0.98 };

export default function CostPerO2Page() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('cost_per_o2');
  const [spec, setSpec] = useState<ModSpec>(() => ({ ...MOD_DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, MOD_DEFAULTS) }));
  const [moduleCount, setModuleCount] = useState(20);
  const [horizonYears, setHorizon] = useState(20);
  const [capexPerModule, setCapex] = useState(1800);
  const [opexPerKWh, setKWh] = useState(0.18);
  const [discountPct, setDiscount] = useState(4);
  const [dutyCyclePct, setDuty] = useState(55);

  useEffect(() => {
    const unsub = onPersistChange(k => {
      if (k === KEYS.DIFFUSER_MODULE) setSpec({ ...MOD_DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, MOD_DEFAULTS) });
    });
    return () => unsub();
  }, []);

  const result = useMemo(() => {
    const dSpec = specFromLegacy(spec);
    const H_eff = Math.max(0.3, spec.waterDepthM - spec.mountingHeightM);
    const AOTR_mod_kgH = moduleAOTRkgh(dSpec, H_eff, spec.waterTempC, spec.alphaFactor ?? 0.85, spec.betaFactor ?? 0.98, spec.currentDoMgL ?? 2.0);
    const kw_mod = blowerKwForModule(dSpec, H_eff);

    // Lifetime cost
    const capex_total = capexPerModule * moduleCount + 25000 + (1200 * moduleCount * kw_mod);   // controls + blowers
    const hours_per_year = 8760 * (dutyCyclePct / 100);
    const annual_kWh = kw_mod * moduleCount * hours_per_year;
    const annual_opex = annual_kWh * opexPerKWh + (50 * moduleCount);    // electricity + maintenance/module/yr

    // NPV of OPEX over horizon at the chosen discount rate (HM Treasury 4 %)
    const r = discountPct / 100;
    const npv_factor = r > 0 ? (1 - Math.pow(1 + r, -horizonYears)) / r : horizonYears;
    const opex_npv = annual_opex * npv_factor;
    const lifetime_total = capex_total + opex_npv;

    // O₂ delivered lifetime (kg)
    const O2_per_year = AOTR_mod_kgH * moduleCount * hours_per_year;        // kg / yr
    const O2_lifetime_kg = O2_per_year * horizonYears;
    const O2_lifetime_mg = O2_lifetime_kg * 1e6;

    // Headline cost per mg O₂ (£ / mg)
    const cost_per_mg_O2 = lifetime_total / Math.max(1, O2_lifetime_mg);
    const cost_per_kg_O2 = cost_per_mg_O2 * 1e6;

    return {
      AOTR_mod_kgH, kw_mod,
      capex_total, annual_kWh, annual_opex, opex_npv, lifetime_total,
      O2_per_year, O2_lifetime_kg, cost_per_mg_O2, cost_per_kg_O2,
    };
  }, [spec, moduleCount, horizonYears, capexPerModule, opexPerKWh, discountPct, dutyCyclePct]);

  const fmt = (n: number, d = 2) => isFinite(n) ? n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={PoundSterling}
        title="Module — Cost per mg O₂ optimisation"
        subtitle="Bryant 2011 / Gantzer 2009 lifetime cost-efficiency metric"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${
              enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'
            }`}>
              <input type="checkbox" checked={enabled} onChange={() => toggle('cost_per_o2')} className="accent-emerald-500" />
              Module {enabled ? 'ENABLED' : 'DISABLED'}
            </label>
            <button onClick={() => navigate('/modules')}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Modules
            </button>
          </div>
        }
      />

      <ModuleConflictBanner moduleId="cost_per_o2" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border-2 border-rb-brand/40 bg-rb-brand/10 p-4">
          <div className="text-xs uppercase tracking-wider text-rb-brand">Lifetime £ / kg O₂</div>
          <div className="text-3xl font-bold text-slate-100 mt-1">£{fmt(result.cost_per_kg_O2, 2)}</div>
          <div className="text-xs text-slate-400 mt-1">key efficiency metric</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">CAPEX total</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">£{fmt(result.capex_total, 0)}</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">OPEX NPV ({horizonYears}-yr)</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">£{fmt(result.opex_npv, 0)}</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Lifetime total</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">£{fmt(result.lifetime_total, 0)}</div>
          <div className="text-xs text-slate-500 mt-1">{moduleCount} modules</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          {[
            { k: 'moduleCount', label: 'Module count', val: moduleCount, set: setModuleCount, step: 1 },
            { k: 'horizonYears', label: 'Horizon (years)', val: horizonYears, set: setHorizon, step: 1 },
            { k: 'capexPerModule', label: 'CAPEX per module (£)', val: capexPerModule, set: setCapex, step: 100 },
            { k: 'opexPerKWh', label: 'Electricity (£/kWh)', val: opexPerKWh, set: setKWh, step: 0.01 },
            { k: 'discountPct', label: 'Discount rate (%)', val: discountPct, set: setDiscount, step: 0.5 },
            { k: 'dutyCyclePct', label: 'Duty cycle (%)', val: dutyCyclePct, set: setDuty, step: 5 },
          ].map(f => (
            <div key={f.k}>
              <label className="text-xs text-slate-400 block mb-1">{f.label}</label>
              <input type="number" step={f.step} value={f.val}
                onChange={e => f.set(Number(e.target.value))}
                className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Detailed breakdown</div>
        <table className="min-w-full text-xs">
          <tbody>
            {[
              ['Per-module AOTR', `${fmt(result.AOTR_mod_kgH, 3)} kg O₂/h`, 'Stenstrom + α-β-T correction'],
              ['Per-module blower kW', `${fmt(result.kw_mod, 2)} kW`, 'isentropic, η=0.70'],
              ['Annual O₂ delivered', `${fmt(result.O2_per_year, 0)} kg/y`, `${dutyCyclePct} % duty cycle`],
              ['Lifetime O₂ delivered', `${fmt(result.O2_lifetime_kg, 0)} kg`, `over ${horizonYears} years`],
              ['Annual electricity', `${fmt(result.annual_kWh, 0)} kWh/y`, `£${fmt(result.annual_kWh * opexPerKWh, 0)}/y`],
              ['Annual OPEX', `£${fmt(result.annual_opex, 0)}/y`, 'electricity + maintenance'],
            ].map(([k, v, src], i) => (
              <tr key={i} className="border-b border-slate-800">
                <td className="px-2 py-1.5 text-slate-400">{k}</td>
                <td className="px-2 py-1.5 text-right text-slate-100 font-semibold tabular-nums">{v}</td>
                <td className="px-2 py-1.5 text-slate-500 italic">{src}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-rb-brand" /> What this module does when enabled
        </div>
        <div className="text-xs text-slate-400 leading-relaxed space-y-2">
          <p>
            Bryant et al. 2011 and Gantzer et al. 2009 use the <strong>£ / kg O₂ delivered over lifetime</strong>
            metric to identify the most efficient placement scheme. The conclusion is consistent: <strong>redistributing
            existing modules to deeper / more critical positions usually outperforms adding more modules</strong>
            at the same total airflow.
          </p>
          <p>
            <strong className="text-emerald-300">Downstream impact when enabled:</strong> The Diffuser Comparison
            page adds a "£/kg O₂" column and re-sorts candidate diffusers by lifetime efficiency rather than
            overall composite score. The Master Report PDF includes the efficiency metric in the headline
            economic summary.
          </p>
          <p>
            <strong className="text-amber-300">Current scenario:</strong> £{fmt(result.cost_per_kg_O2, 2)} per kg O₂
            delivered. Typical industry benchmark for diffused aeration is £8–£15 per kg O₂; a figure outside
            this band warrants review.
          </p>
          <p className="italic text-slate-500">References: Bryant, Gantzer & Little 2011; Gantzer, Bryant & Little 2009; HM Treasury Green Book 2022.</p>
        </div>
      </div>
    </div>
  );
}
